import React, { useState, useEffect, useRef } from 'react';
import { getAmbulanceRunningReport, downloadAmbulanceRunningReportPDF, downloadAmbulanceRunningReportExcel, AmbulanceRunningReportData } from '../../services/api';
import { getMaxDateToday, getMaxEndDateForStart } from '../../utils/dateUtils';

type DateFilterType = 'today' | 'yesterday' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth' | 'custom';

const AmbulanceRunningReport: React.FC = () => {
  const [vehicleData, setVehicleData] = useState<AmbulanceRunningReportData[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string>('');
  const [downloadError, setDownloadError] = useState<string>('');
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const downloadMenuRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Date filter state
  const [dateFilterType, setDateFilterType] = useState<DateFilterType>('today');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');

  // Monitor theme changes
  useEffect(() => {
    const checkTheme = () => {
      setIsDarkTheme(document.documentElement.getAttribute('data-theme') === 'dark');
    };
    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  // Close download menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(event.target as Node)) {
        setShowDownloadMenu(false);
      }
    };

    if (showDownloadMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDownloadMenu]);

  useEffect(() => {
    fetchVehicleData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilterType, customStartDate, customEndDate]);

  // Compute date range based on filter selection
  const getDateRange = (): { startDate: string; endDate: string } | null => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const toDateStr = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${dd}`;
    };

    switch (dateFilterType) {
      case 'today': {
        const d = toDateStr(today);
        return { startDate: d, endDate: d };
      }

      case 'yesterday': {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const d = toDateStr(yesterday);
        return { startDate: d, endDate: d };
      }

      case 'thisWeek': {
        const start = new Date(today);
        const dayOfWeek = start.getDay();
        const diff = start.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        start.setDate(diff);
        return { startDate: toDateStr(start), endDate: toDateStr(today) };
      }

      case 'lastWeek': {
        const start = new Date(today);
        const dayOfWeek = start.getDay();
        const diff = start.getDate() - dayOfWeek - 6;
        start.setDate(diff);
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        return { startDate: toDateStr(start), endDate: toDateStr(end) };
      }

      case 'thisMonth': {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        return { startDate: toDateStr(start), endDate: toDateStr(today) };
      }

      case 'lastMonth': {
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const end = new Date(now.getFullYear(), now.getMonth(), 0);
        return { startDate: toDateStr(start), endDate: toDateStr(end) };
      }

      case 'custom': {
        if (!customStartDate || !customEndDate) return null;
        const maxEndStr = getMaxEndDateForStart(customStartDate);
        const endDate = maxEndStr && customEndDate > maxEndStr ? maxEndStr : customEndDate;
        return { startDate: customStartDate, endDate };
      }

      default:
        return null;
    }
  };

  // Get all dates in the current filter range for column headers
  const getDateColumns = (): Date[] => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (dateFilterType) {
      case 'today':
        return [new Date(today)];

      case 'yesterday': {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return [yesterday];
      }

      case 'thisWeek': {
        const start = new Date(today);
        const dayOfWeek = start.getDay();
        const diff = start.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        start.setDate(diff);
        const dates = [];
        const current = new Date(start);
        while (current <= today) {
          dates.push(new Date(current));
          current.setDate(current.getDate() + 1);
        }
        return dates;
      }

      case 'lastWeek': {
        const start = new Date(today);
        const dayOfWeek = start.getDay();
        const diff = start.getDate() - dayOfWeek - 6;
        start.setDate(diff);
        const dates = [];
        const current = new Date(start);
        for (let i = 0; i < 7; i++) {
          dates.push(new Date(current));
          current.setDate(current.getDate() + 1);
        }
        return dates;
      }

      case 'thisMonth': {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        const dates = [];
        const current = new Date(start);
        while (current <= today) {
          dates.push(new Date(current));
          current.setDate(current.getDate() + 1);
        }
        return dates;
      }

      case 'lastMonth': {
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const end = new Date(now.getFullYear(), now.getMonth(), 0);
        const dates = [];
        const current = new Date(start);
        while (current <= end) {
          dates.push(new Date(current));
          current.setDate(current.getDate() + 1);
        }
        return dates;
      }

      case 'custom': {
        if (!customStartDate || !customEndDate) return [];
        const start = new Date(customStartDate + 'T00:00:00');
        const end = new Date(customEndDate + 'T00:00:00');
        const dates = [];
        const current = new Date(start);
        while (current <= end) {
          dates.push(new Date(current));
          current.setDate(current.getDate() + 1);
        }
        return dates;
      }

      default:
        return [];
    }
  };

  const formatDateDDMMYY = (date: Date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
  };

  const toDateKey = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const fetchVehicleData = async () => {
    const range = getDateRange();
    if (!range) {
      setVehicleData([]);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await getAmbulanceRunningReport(undefined, range.startDate, range.endDate);
      const data = response.data.data || [];
      setVehicleData(data);
    } catch (err: any) {
      console.error('Error fetching ambulance running report:', err);
      setError(err.response?.data?.message || 'Failed to load ambulance running report');
    } finally {
      setLoading(false);
    }
  };

  const filteredVehicleData = vehicleData.filter((item) => {
    if (!searchTerm.trim()) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      item.ambulanceName?.toLowerCase().includes(searchLower) ||
      item.licensePlate?.toLowerCase().includes(searchLower) ||
      item.type?.toLowerCase().includes(searchLower)
    );
  });

  const handleDownload = async (format: 'pdf' | 'excel') => {
    try {
      setDownloading(true);
      setDownloadError('');
      setShowDownloadMenu(false);

      const range = getDateRange();
      if (!range) {
        setDownloadError('Please select a valid date range');
        setDownloading(false);
        return;
      }

      let response;
      let fileExtension;

      if (format === 'pdf') {
        response = await downloadAmbulanceRunningReportPDF(undefined, range.startDate, range.endDate);
        fileExtension = 'pdf';
      } else {
        response = await downloadAmbulanceRunningReportExcel(undefined, range.startDate, range.endDate);
        fileExtension = 'xlsx';
      }

      const { url } = response.data.data;

      const link = document.createElement('a');
      link.href = url;
      const dateRangeStr = dateFilterType === 'custom'
        ? `${customStartDate}_to_${customEndDate}`
        : dateFilterType;
      link.download = `ambulance-running-report-${dateRangeStr}.${fileExtension}`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: any) {
      console.error(`Error downloading ${format.toUpperCase()}:`, err);
      setDownloadError(err.response?.data?.message || `Failed to download ${format.toUpperCase()}`);
    } finally {
      setDownloading(false);
    }
  };

  const dateColumns = getDateColumns();
  const showDateColumns = dateColumns.length > 0;

  if (loading) {
    return (
      <div>
        <div className="loading">Loading ambulance data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="empty-state">
          <h3>Error</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: downloadError ? '10px' : '20px', flexWrap: 'wrap', gap: '15px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'nowrap' }}>
          <input
            type="text"
            placeholder="Search by name, license plate, or type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              fontSize: '16px',
              width: '220px',
              flex: '0 0 220px',
              border: '1px solid',
              borderColor: isDarkTheme ? '#334155' : '#ddd',
              height: '36px',
              boxSizing: 'border-box',
            }}
          />
          <select
            value={dateFilterType}
            onChange={(e) => setDateFilterType(e.target.value as DateFilterType)}
            className="reports-filter-select"
            style={{ padding: '8px 12px', borderRadius: '6px', fontSize: '16px', minWidth: '150px' }}
          >
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="thisWeek">This Week</option>
            <option value="lastWeek">Last Week</option>
            <option value="thisMonth">This Month</option>
            <option value="lastMonth">Last Month</option>
            <option value="custom">Custom Dates</option>
          </select>
          <div style={{ position: 'relative' }} ref={downloadMenuRef}>
            <button
              onClick={() => setShowDownloadMenu(!showDownloadMenu)}
              disabled={loading || downloading || vehicleData.length === 0}
              style={{
                padding: '10px 20px',
                backgroundColor: '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: loading || downloading || vehicleData.length === 0 ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                opacity: loading || downloading || vehicleData.length === 0 ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {downloading ? 'Generating...' : 'Download'}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '6px' }}>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </button>
            {showDownloadMenu && (
              <div style={{
                position: 'absolute', top: '100%', right: 0, marginTop: '5px',
                backgroundColor: isDarkTheme ? '#1e293b' : '#ffffff',
                border: isDarkTheme ? '1px solid #334155' : '1px solid #ddd',
                borderRadius: '6px', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', zIndex: 1000, minWidth: '150px',
              }}>
                <button onClick={() => handleDownload('pdf')} disabled={downloading}
                  style={{ width: '100%', padding: '10px 15px', backgroundColor: 'transparent', color: isDarkTheme ? '#ffffff' : '#333', border: 'none', borderRadius: '6px 6px 0 0', cursor: downloading ? 'not-allowed' : 'pointer', fontSize: '16px', textAlign: 'left', opacity: downloading ? 0.6 : 1 }}
                  onMouseEnter={(e) => { if (!downloading) e.currentTarget.style.backgroundColor = isDarkTheme ? '#334155' : '#f5f5f5'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >Download as PDF</button>
                <button onClick={() => handleDownload('excel')} disabled={downloading}
                  style={{ width: '100%', padding: '10px 15px', backgroundColor: 'transparent', color: isDarkTheme ? '#ffffff' : '#333', border: 'none', borderTop: isDarkTheme ? '1px solid #334155' : '1px solid #ddd', borderRadius: '0 0 6px 6px', cursor: downloading ? 'not-allowed' : 'pointer', fontSize: '16px', textAlign: 'left', opacity: downloading ? 0.6 : 1 }}
                  onMouseEnter={(e) => { if (!downloading) e.currentTarget.style.backgroundColor = isDarkTheme ? '#334155' : '#f5f5f5'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >Download as Excel</button>
              </div>
            )}
          </div>
        </div>
        {dateFilterType === 'custom' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'nowrap' }}>
              <label className="reports-filter-label" style={{ fontSize: '16px', fontWeight: '500', whiteSpace: 'nowrap' }}>Start Date:</label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => {
                  const value = e.target.value;
                  setCustomStartDate(value);
                  if (value && customEndDate) {
                    const start = new Date(value);
                    const end = new Date(customEndDate);
                    const diffDays = Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
                    if (diffDays > 31) setCustomEndDate(getMaxEndDateForStart(value));
                  }
                }}
                max={getMaxDateToday()}
                className="reports-date-input"
                style={{ padding: '8px 12px', borderRadius: '6px', fontSize: '16px' }}
              />
              <label className="reports-filter-label" style={{ fontSize: '16px', fontWeight: '500', whiteSpace: 'nowrap' }}>End Date:</label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                min={customStartDate || undefined}
                max={customStartDate ? getMaxEndDateForStart(customStartDate) : getMaxDateToday()}
                className="reports-date-input"
                style={{ padding: '8px 12px', borderRadius: '6px', fontSize: '16px' }}
              />
            </div>
          )}
      </div>
      {downloadError && (
        <div style={{ padding: '10px', backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '4px', color: '#c33', fontSize: '16px', marginBottom: '20px' }}>
          {downloadError}
        </div>
      )}
      {filteredVehicleData.length === 0 ? (
        <div className="empty-state">
          <h3>{vehicleData.length === 0 ? 'No ambulances found' : 'No matching results'}</h3>
          <p>{vehicleData.length === 0 ? 'No ambulances are registered in the system' : 'Try adjusting your search criteria'}</p>
        </div>
      ) : (
        <div
          className="table-container"
          style={{
            height: 'calc(100vh - 360px)',
            overflowY: 'auto',
            overflowX: 'auto',
            border: isDarkTheme ? '1px solid #334155' : '1px solid #e2e8f0',
            backgroundColor: isDarkTheme ? '#1e293b' : '#ffffff',
          }}
        >
          <table
            className="bookings-table"
            style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, minWidth: '800px' }}
          >
            <thead>
              <tr>
                <th style={{
                  padding: '8px 10px', textAlign: 'left', fontWeight: '600', fontSize: '15px',
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                  color: isDarkTheme ? '#94a3b8' : '#64748b',
                  backgroundColor: isDarkTheme ? '#0f172a' : '#f8fafc',
                  borderBottom: isDarkTheme ? '2px solid #334155' : '2px solid #e2e8f0',
                  position: 'sticky', left: 0, zIndex: 10,
                  width: '160px', minWidth: '160px', maxWidth: '160px', wordBreak: 'break-word',
                }}>Ambulance Name</th>
                <th style={{
                  padding: '8px 10px', textAlign: 'left', fontWeight: '600', fontSize: '15px',
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                  color: isDarkTheme ? '#94a3b8' : '#64748b',
                  backgroundColor: isDarkTheme ? '#0f172a' : '#f8fafc',
                  borderBottom: isDarkTheme ? '2px solid #334155' : '2px solid #e2e8f0',
                  whiteSpace: 'nowrap',
                }}>License Plate</th>
                <th style={{
                  padding: '8px 10px', textAlign: 'left', fontWeight: '600', fontSize: '15px',
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                  color: isDarkTheme ? '#94a3b8' : '#64748b',
                  backgroundColor: isDarkTheme ? '#0f172a' : '#f8fafc',
                  borderBottom: isDarkTheme ? '2px solid #334155' : '2px solid #e2e8f0',
                  whiteSpace: 'nowrap',
                }}>Type</th>
                {showDateColumns && dateColumns.map((date, idx) => (
                  <th
                    key={idx}
                    style={{
                      padding: '8px 6px', textAlign: 'center', fontWeight: '600', fontSize: '14px',
                      textTransform: 'uppercase', letterSpacing: '0.5px',
                      color: isDarkTheme ? '#94a3b8' : '#64748b',
                      backgroundColor: isDarkTheme ? '#0f172a' : '#f8fafc',
                      borderBottom: isDarkTheme ? '2px solid #334155' : '2px solid #e2e8f0',
                      borderLeft: isDarkTheme ? '1px solid #334155' : '1px solid #e2e8f0',
                      minWidth: '160px',
                    }}
                  >
                    {formatDateDDMMYY(date)}
                  </th>
                ))}
                <th style={{
                  padding: '8px 10px', textAlign: 'center', fontWeight: '600', fontSize: '15px',
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                  color: isDarkTheme ? '#94a3b8' : '#64748b',
                  backgroundColor: isDarkTheme ? '#0f172a' : '#f8fafc',
                  borderBottom: isDarkTheme ? '2px solid #334155' : '2px solid #e2e8f0',
                  borderLeft: isDarkTheme ? '1px solid #334155' : '1px solid #e2e8f0',
                  whiteSpace: 'nowrap', minWidth: '160px',
                }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {filteredVehicleData.map((item, rowIdx) => {
                const baseBgColor = rowIdx % 2 === 0
                  ? (isDarkTheme ? '#1e293b' : '#ffffff')
                  : (isDarkTheme ? '#0f172a' : '#f8fafc');
                const hoverBgColor = isDarkTheme ? '#334155' : '#f1f5f9';

                return (
                  <tr
                    key={item.ambulanceId}
                    style={{ backgroundColor: baseBgColor, transition: 'background-color 0.2s ease' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = hoverBgColor;
                      const nameCell = e.currentTarget.querySelector('td:first-child') as HTMLElement;
                      if (nameCell) nameCell.style.backgroundColor = hoverBgColor;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = baseBgColor;
                      const nameCell = e.currentTarget.querySelector('td:first-child') as HTMLElement;
                      if (nameCell) nameCell.style.backgroundColor = baseBgColor;
                    }}
                  >
                    <td style={{
                      padding: '8px 10px',
                      borderBottom: isDarkTheme ? '1px solid #334155' : '1px solid #e2e8f0',
                      color: isDarkTheme ? '#f1f5f9' : '#1e293b',
                      fontWeight: '500', fontSize: '16px',
                      position: 'sticky', left: 0,
                      backgroundColor: baseBgColor, zIndex: 1,
                      width: '160px', minWidth: '160px', maxWidth: '160px', wordBreak: 'break-word',
                      transition: 'background-color 0.2s ease',
                    }}>{item.ambulanceName}</td>
                    <td style={{
                      padding: '8px 10px',
                      borderBottom: isDarkTheme ? '1px solid #334155' : '1px solid #e2e8f0',
                      color: isDarkTheme ? '#cbd5e1' : '#475569', fontSize: '15px',
                    }}>{item.licensePlate || '—'}</td>
                    <td style={{
                      padding: '8px 10px',
                      borderBottom: isDarkTheme ? '1px solid #334155' : '1px solid #e2e8f0',
                      color: isDarkTheme ? '#cbd5e1' : '#475569', fontSize: '15px',
                    }}>{item.type || '—'}</td>
                    {showDateColumns && dateColumns.map((date, idx) => {
                      const dateKey = toDateKey(date);
                      const d = item.dailyData?.[dateKey];
                      const km = d?.km || 0;
                      const trips = d?.trips || 0;
                      const timeHrs = d?.timeTravelledHrs || 0;
                      const engHrs = d?.engineHours || 0;
                      const fRefill = d?.fuelRefill || 0;
                      const fConsumed = d?.fuelConsumed || 0;
                      const fmtTime = (h: number) => { const hrs = Math.floor(h); const mins = Math.round((h - hrs) * 60); return `${hrs}h ${mins}m`; };
                      const dimStyle: React.CSSProperties = { color: isDarkTheme ? '#475569' : '#cbd5e1', fontStyle: 'italic' };
                      return (
                        <td
                          key={idx}
                          style={{
                            padding: '6px 8px',
                            borderBottom: isDarkTheme ? '1px solid #334155' : '1px solid #e2e8f0',
                            borderLeft: isDarkTheme ? '1px solid #334155' : '1px solid #e2e8f0',
                            verticalAlign: 'top',
                          }}
                        >
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', fontSize: '12px', whiteSpace: 'nowrap' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '6px' }}>
                              <span style={{ color: isDarkTheme ? '#94a3b8' : '#64748b', fontWeight: 500 }}>Distance</span>
                              {km > 0
                                ? <span style={{ fontWeight: 600, color: isDarkTheme ? '#6ee7b7' : '#059669' }}>{km.toFixed(2)} km</span>
                                : <span style={dimStyle}>—</span>}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '6px' }}>
                              <span style={{ color: isDarkTheme ? '#94a3b8' : '#64748b', fontWeight: 500 }}>Trips</span>
                              {trips > 0
                                ? <span style={{ fontWeight: 600, color: isDarkTheme ? '#93c5fd' : '#2563eb' }}>{trips}</span>
                                : <span style={dimStyle}>0</span>}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '6px' }}>
                              <span style={{ color: isDarkTheme ? '#94a3b8' : '#64748b', fontWeight: 500 }}>Running Time</span>
                              {timeHrs > 0
                                ? <span style={{ color: isDarkTheme ? '#cbd5e1' : '#475569' }}>{fmtTime(timeHrs)}</span>
                                : <span style={dimStyle}>—</span>}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '6px' }}>
                              <span style={{ color: isDarkTheme ? '#94a3b8' : '#64748b', fontWeight: 500 }}>Engine Hours</span>
                              {engHrs > 0
                                ? <span style={{ color: isDarkTheme ? '#cbd5e1' : '#475569' }}>{fmtTime(engHrs)}</span>
                                : <span style={dimStyle}>—</span>}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '6px' }}>
                              <span style={{ color: isDarkTheme ? '#94a3b8' : '#64748b', fontWeight: 500 }}>Fuel Refilld</span>
                              {fRefill > 0
                                ? <span style={{ fontWeight: 600, color: isDarkTheme ? '#6ee7b7' : '#059669' }}>{fRefill.toFixed(1)} L</span>
                                : <span style={dimStyle}>—</span>}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '6px' }}>
                              <span style={{ color: isDarkTheme ? '#94a3b8' : '#64748b', fontWeight: 500 }}>Fuel Consumed</span>
                              {fConsumed > 0
                                ? <span style={{ fontWeight: 600, color: isDarkTheme ? '#fca5a5' : '#dc2626' }}>{fConsumed.toFixed(1)} L</span>
                                : <span style={dimStyle}>—</span>}
                            </div>
                          </div>
                        </td>
                      );
                    })}
                    {/* Total column — stacked same as per-date */}
                    {(() => {
                      const fmtTime = (h: number) => { const hrs = Math.floor(h); const mins = Math.round((h - hrs) * 60); return `${hrs}h ${mins}m`; };
                      const dimStyle: React.CSSProperties = { color: isDarkTheme ? '#475569' : '#cbd5e1', fontStyle: 'italic' };
                      return (
                        <td style={{
                          padding: '6px 8px', verticalAlign: 'top',
                          borderBottom: isDarkTheme ? '1px solid #334155' : '1px solid #e2e8f0',
                          borderLeft: isDarkTheme ? '1px solid #334155' : '1px solid #e2e8f0',
                        }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', fontSize: '12px', whiteSpace: 'nowrap' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '6px' }}>
                              <span style={{ color: isDarkTheme ? '#94a3b8' : '#64748b', fontWeight: 500 }}>Distance</span>
                              {item.totalKm > 0
                                ? <span style={{ fontWeight: 600, color: isDarkTheme ? '#6ee7b7' : '#059669' }}>{item.totalKm.toFixed(2)} km</span>
                                : <span style={dimStyle}>—</span>}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '6px' }}>
                              <span style={{ color: isDarkTheme ? '#94a3b8' : '#64748b', fontWeight: 500 }}>Trips</span>
                              {item.totalTrips > 0
                                ? <span style={{ fontWeight: 600, color: isDarkTheme ? '#93c5fd' : '#2563eb' }}>{item.totalTrips}</span>
                                : <span style={dimStyle}>0</span>}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '6px' }}>
                              <span style={{ color: isDarkTheme ? '#94a3b8' : '#64748b', fontWeight: 500 }}>Running Time</span>
                              {item.totalTimeTravelledHrs > 0
                                ? <span style={{ color: isDarkTheme ? '#cbd5e1' : '#475569' }}>{fmtTime(item.totalTimeTravelledHrs)}</span>
                                : <span style={dimStyle}>—</span>}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '6px' }}>
                              <span style={{ color: isDarkTheme ? '#94a3b8' : '#64748b', fontWeight: 500 }}>Engine Hours</span>
                              {item.totalEngineHours > 0
                                ? <span style={{ color: isDarkTheme ? '#cbd5e1' : '#475569' }}>{fmtTime(item.totalEngineHours)}</span>
                                : <span style={dimStyle}>—</span>}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '6px' }}>
                              <span style={{ color: isDarkTheme ? '#94a3b8' : '#64748b', fontWeight: 500 }}>Fuel Refilld</span>
                              {item.totalFuelRefill > 0
                                ? <span style={{ fontWeight: 600, color: isDarkTheme ? '#6ee7b7' : '#059669' }}>{item.totalFuelRefill.toFixed(1)} L</span>
                                : <span style={dimStyle}>—</span>}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '6px' }}>
                              <span style={{ color: isDarkTheme ? '#94a3b8' : '#64748b', fontWeight: 500 }}>Fuel Consumed</span>
                              {item.totalFuelConsumed > 0
                                ? <span style={{ fontWeight: 600, color: isDarkTheme ? '#fca5a5' : '#dc2626' }}>{item.totalFuelConsumed.toFixed(1)} L</span>
                                : <span style={dimStyle}>—</span>}
                            </div>
                          </div>
                        </td>
                      );
                    })()}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AmbulanceRunningReport;
